import type { EmailStatus } from './api';
import { currentLocale, t } from './i18n';

export const STATUS_LABEL: Record<EmailStatus, string> = {
  queued: 'В очереди',
  sent: 'Передано',
  delivered: 'Доставлено',
  bounced: 'Отскок',
  complained: 'Жалоба',
  failed: 'Ошибка',
  delivery_unknown: 'Неизвестно',
  suppressed: 'Подавлено',
  cancelled: 'Отменено MailOcean',
};

export const STATUS_HINT: Record<EmailStatus, string> = {
  queued: 'Принято API, ждёт передачи провайдеру',
  sent: 'Провайдер принял письмо',
  delivered: 'Почтовый сервер получателя подтвердил приём',
  bounced: 'Постоянный отказ сервера получателя',
  complained: 'Получатель отметил письмо как спам — добавлен в подавления',
  failed: 'Провайдер окончательно отклонил письмо',
  delivery_unknown: 'Результат передачи неизвестен; повторно не отправляем, чтобы не создать дубль',
  suppressed: 'Адрес в списке подавлений — письмо не отправлялось',
  cancelled: 'Отменено сотрудником MailOcean до передачи провайдеру — письмо не отправлялось',
};

export const EVENT_LABEL: Record<string, string> = {
  received: 'Принято API',
  queued: 'Поставлено в очередь',
  suppressed: 'Подавлено',
  handed_to_provider: 'Передано провайдеру',
  provider_accepted: 'Провайдер принял',
  retry_scheduled: 'Повтор запланирован',
  delivery_unknown: 'Результат неизвестен',
  failed: 'Отклонено',
  delivered: 'Доставлено получателю',
  bounced: 'Постоянный отказ сервера получателя',
  soft_bounced: 'Мягкий отскок',
  deferred: 'Отложено сервером получателя',
  complained: 'Жалоба на спам',
  cancelled: 'Отменено MailOcean',
  reconciled: 'Исход сверен поддержкой MailOcean',
};

export function eventTone(type: string): 'good' | 'bad' | 'warn' | '' {
  if (['provider_accepted', 'delivered'].includes(type)) return 'good';
  if (['failed', 'bounced', 'complained'].includes(type)) return 'bad';
  if (['delivery_unknown', 'retry_scheduled', 'soft_bounced', 'suppressed', 'deferred'].includes(type)) return 'warn';
  return '';
}

/** What a refusal means, by data.bounce_category (webhooks and GET /v1/emails/:id carry the same value). */
export const BOUNCE_CATEGORY_LABEL: Record<string, string> = {
  recipient: 'Адреса не существует или ящик отключён',
  mailbox_full: 'Ящик переполнен',
  sender_reputation: 'Сервер получателя ограничил отправителя по репутации',
  authentication: 'Не прошла проверка SPF/DKIM/DMARC домена отправителя',
  sender_infrastructure: 'Проблема инфраструктуры отправки (PTR/TLS) — мы разбираемся',
  content: 'Отказ из-за содержимого или ссылок',
  message_format: 'Письмо нарушает формат (RFC 5322)',
  recipient_policy: 'Организация получателя не принимает такие письма (нужен allowlist)',
  policy: 'Отказ по политике сервера получателя',
  rate_limited: 'Сервер получателя ограничил скорость',
  temporary: 'Временная ошибка',
  complaint: 'Получатель пожаловался на спам',
  unknown: 'Причина не распознана — мы разбираем такие ответы',
};

/** Categories about the sender (reputation, authentication, infrastructure, content, policy): never the recipient's fault. */
const SENDER_SIDE = new Set(['sender_reputation', 'authentication', 'sender_infrastructure', 'content', 'message_format', 'policy', 'recipient_policy']);

/** Events the customer sees: provider evidence for an UNKNOWN message is a hint for MailOcean staff only. */
export function customerEvents<T extends { type: string }>(events: T[]): T[] {
  return events.filter((e) => e.type !== 'provider_evidence');
}

/**
 * Lines under a timeline event (BUILD-SPEC §5.5): what the server's answer means, whether the address was suppressed
 * (only when the platform says so) and the server's own words.
 */
export function eventExplanation(e: { type: string; data: Record<string, unknown> }): string[] {
  const d = e.data ?? {};
  const out: string[] = [];
  if (e.type === 'deferred') out.push(t('Сервер получателя попросил повторить позже — повтор идёт'));
  if (e.type === 'failed' && d.reason === 'expired') out.push(t('Не доставлено: истёк срок повторов'));
  const category = typeof d.bounce_category === 'string' ? d.bounce_category : null;
  if (category && (e.type === 'bounced' || e.type === 'soft_bounced' || e.type === 'failed' || e.type === 'complained' || e.type === 'deferred')) {
    const label = BOUNCE_CATEGORY_LABEL[category];
    out.push(label ? t(label) : category);
  }
  const raw = typeof d.explanation_ru === 'string' ? d.explanation_ru : '';
  // A class text may say «Адрес добавлен в подавления» while this event did not suppress (a temporary report with a
  // 5.1.1 code): the fact wins, the sentence goes.
  const explanation = d.suppressed === true ? raw : raw.replace(/\s*[^.;]*добавлен в подавления[^.]*[.;]?/gi, '').trim();
  if (explanation) out.push(explanation);
  // The suppression line follows data.suppressed (the platform's decision); «добавлен» is not repeated when the rule's
  // text already says it, «не подавлен» is always stated.
  const says = (s: string) => explanation.toLowerCase().includes(s);
  if (e.type === 'bounced' || e.type === 'soft_bounced' || (e.type === 'failed' && category)) {
    if (d.suppressed === true && !says('добавлен в подавления')) out.push(t('Адрес добавлен в подавления'));
    else if (d.suppressed === false) out.push(category && SENDER_SIDE.has(category) ? t('Адрес не подавлен: причина на стороне отправителя, а не получателя') : t('Адрес не подавлен'));
  }
  if (e.type === 'complained' && d.suppressed === true && !says('добавлен в подавления')) out.push(t('Адрес добавлен в подавления'));
  return out;
}

/** The server's reply as the provider reported it (the customer's own message, shown as is). */
export function serverReply(d: Record<string, unknown>): string | null {
  const parts = [d.smtp_code, d.smtp_status, d.diagnostic].filter((x) => typeof x === 'string' || typeof x === 'number').map(String);
  return parts.length ? parts.join(' ') : null;
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' });
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString(currentLocale(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export const pct = (x: number, digits = 1) => `${(x * 100).toFixed(digits)}%`;
export const num = (n: number) => n.toLocaleString(currentLocale());
export const ms = (n: number | null) => (n == null ? '—' : n < 1000 ? `${n} мс` : `${(n / 1000).toFixed(2)} с`);
export const bytes = (n: number) => (n < 1024 ? `${n} Б` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} КБ` : `${(n / 1024 / 1024).toFixed(1)} МБ`);
export const money = (cents: number, currency = 'EUR') =>
  `${(cents / 100).toLocaleString(currentLocale(), { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })} ${currency === 'EUR' ? '€' : currency}`;
export const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long', year: 'numeric' }) : '—');
